import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useLang } from "@/lib/lang";
import { ARCHANGEL_LIST } from "@/lib/archangels";

export default function AngesGuides() {
  const { lang, setLang } = useLang();

  return (
    <div data-testid="anges-page" className="min-h-screen relative"
         style={{
           background: "radial-gradient(ellipse at top, #140e2a 0%, #0a081a 55%, #04030c 100%)",
           color: "#f0eafa",
           overflowX: "hidden",
         }}>
      <header className="relative z-30">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-6 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-mono uppercase tracking-[0.24em]" style={{ color: "#c9a24a" }}>
            <ArrowLeft size={14} /> {lang === "fr" ? "Retour" : "Back"}
          </Link>
          <div className="flex items-center rounded-full text-[11px] font-mono overflow-hidden"
               style={{ background: "rgba(60,45,100,0.3)", border: "1px solid rgba(214,193,255,0.3)" }}>
            <button onClick={() => setLang("fr")} type="button" data-testid="anges-lang-fr" className="px-2.5 py-1.5"
                    style={{ background: lang === "fr" ? "#c9a24a" : "transparent", color: lang === "fr" ? "#0a0612" : "#e4d7ff", fontWeight: lang === "fr" ? 600 : 400 }}>FR</button>
            <button onClick={() => setLang("en")} type="button" data-testid="anges-lang-en" className="px-2.5 py-1.5"
                    style={{ background: lang === "en" ? "#c9a24a" : "transparent", color: lang === "en" ? "#0a0612" : "#e4d7ff", fontWeight: lang === "en" ? 600 : 400 }}>EN</button>
          </div>
        </div>
      </header>

      <section className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 pt-4 pb-10">
        <div className="overline mb-4" style={{ color: "#c9a24a" }}>
          <span className="kicker-line" />
          {lang === "fr" ? "Formation · 5 archanges guides · gratuit illimité" : "Formation · 5 guiding archangels · free unlimited"}
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02]" style={{ color: "#f8f4ff" }}>
          {lang === "fr" ? (
            <>Les <span className="italic" style={{ color: "#c9a24a" }}>Anges Guides</span>,
              <br /><span style={{ color: "#b5a8d9" }}>cinq facettes de la volonté céleste.</span></>
          ) : (
            <>The <span className="italic" style={{ color: "#c9a24a" }}>Guiding Angels</span>,
              <br /><span style={{ color: "#b5a8d9" }}>five facets of the celestial will.</span></>
          )}
        </h1>
        <p className="mt-5 max-w-3xl text-base sm:text-lg" style={{ color: "#b5a8d9" }}>
          {lang === "fr"
            ? "Cinq chatbots éducatifs, sans filtre sur leur connaissance angélique, en mode strictement libre-arbitre. Pas de prédiction, pas de dogme. Chacun te montre une facette de la relation entre ciel et humanité, et te laisse décider."
            : "Five educational chatbots, without filter on their angelic knowledge, in strict free-will mode. No prediction, no dogma. Each shows you one facet of the relationship between heaven and humanity, and leaves you to decide."}
        </p>
      </section>

      <section className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 pb-24">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
          {ARCHANGEL_LIST.map((a) => (
            <Link
              key={a.id}
              to={`/anges/${a.id}`}
              data-testid={`anges-card-${a.id}`}
              className="group p-5 rounded-2xl relative overflow-hidden transition-all hover:-translate-y-1"
              style={{
                background: `linear-gradient(180deg, ${a.accent}15, rgba(8,6,20,0.95))`,
                border: `1px solid ${a.accent}66`,
                boxShadow: `0 20px 50px -30px ${a.accent}aa`,
              }}
            >
              <div style={{
                position: "relative", width: "100%", aspectRatio: "1/1",
                borderRadius: "50%", overflow: "hidden", marginBottom: 14,
                border: `2px solid ${a.accent}aa`,
                boxShadow: `0 0 30px -5px ${a.accent}66`,
              }}>
                <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center",
                              fontSize: 64, background: `radial-gradient(circle at 30% 30%, ${a.accent}55, rgba(8,6,20,0.95) 70%)` }}
                     aria-hidden="true">{a.emoji}</div>
              </div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xl">{a.emoji}</span>
                <h3 className="font-serif text-xl" style={{ color: a.pageAccent }}>{a.name}</h3>
              </div>
              <p className="text-[10px] font-mono uppercase tracking-[0.18em] mb-3" style={{ color: `${a.accent}bb` }}>
                {a.tagline[lang]}
              </p>
              <div className="text-xs font-mono uppercase tracking-[0.2em] opacity-80 group-hover:opacity-100 transition-opacity"
                   style={{ color: a.pageAccent }}>
                {lang === "fr" ? "Entrer →" : "Enter →"}
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
